import { LitElement, html, css, styleMap, utils } from '@hh/global.js'

const { debounce } = utils

/**
 * scroll state -1: haven't scrolled yet
 * scroll state 0: initial scroll down
 * scroll state 1: scrolling down, past a certain point
 * scroll state 2: scrolling up
 */

class HHStickyHeader extends LitElement {
  static properties = {
    closeWhenHidden: {
      type: String,
      attribute: 'close-when-hidden',
    },
    _headerHeight: {
      type: Number,
      state: true,
    },
    _announcementHeight: {
      type: Number,
      state: true,
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
    _scrollTop: {
      type: Number,
      state: true,
    },
  }

  constructor() {
    super()
    this.headerBounds = {}
    this.currentScrollTop = 0
    this.closeWhenHidden = 'hh-details-modal'
    this._state = -1

    // Initial sync setup
    this.updateHeaderHeights(true)
  }

  updateHeaderHeights(isInitial = false) {
    const header = document.querySelector('.js-header')
    const announcement = document.querySelector('.announcement-bar')

    if (header) {
      const headerHeight = header.getBoundingClientRect().height
      const announcementHeight =
        announcement?.getBoundingClientRect().height || 0
      const totalHeight = headerHeight + announcementHeight

      // Set CSS variables
      document.documentElement.style.setProperty(
        '--hh-header-height',
        `${headerHeight}px`
      )
      document.documentElement.style.setProperty(
        '--hh-announcement-height',
        `${announcementHeight}px`
      )

      // Set main padding if needed
      if (this.shouldSetMainPadding()) {
        document.querySelector('main').style.paddingTop = `${totalHeight}px`
        document.documentElement.style.setProperty(
          '--hh-header-bottom',
          `${totalHeight}px`
        )
      }

      // Additional setup for non-initial calls
      if (!isInitial) {
        this._state = 0
        this._headerHeight = headerHeight
        this._announcementHeight = announcementHeight
        this._height = announcementHeight
      } else {
        document.body.classList.add('header-loaded')
      }
    }
  }

  shouldSetMainPadding() {
    return !(
      document.body.classList.contains('template-index') ||
      Array.from(document.body.classList).some(
        (c) =>
          c.startsWith('template-customers') &&
          c.indexOf('login') < 0 &&
          c.indexOf('register') < 0 &&
          c.indexOf('reset_password') < 0 &&
          c.indexOf('addresses') < 0
      ) ||
      this.classList.contains('absolute')
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.onScrollHandler = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScrollHandler, { passive: true })
    this.createObserver()

    // Single debounced resize handler using ResizeObserver
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.updateHeaderHeights()
      }, 150)
    )
    this.resizeObserver.observe(document.documentElement)

    // Initial height setup
    setTimeout(() => {
      this.updateHeaderHeights()
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.onScrollHandler)
    this.resizeObserver?.disconnect()
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    this._scrollTop = scrollTop

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll down
      requestAnimationFrame(this.hide.bind(this))
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll up
      requestAnimationFrame(this.reveal.bind(this))
    } else if (scrollTop <= this.headerBounds.top) {
      // at top
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop

    document
      .querySelector('.js-transparent-header[light-text]')
      ?.classList[window.scrollY > 0 ? 'remove' : 'add']('light-text')
    document
      .querySelector('.js-transparent-header')
      ?.classList[window.scrollY > 0 ? 'add' : 'remove']('scrolled')
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    document
      .querySelector(':root')
      .style.setProperty(`--hh-announcement-height`, `0px`)

    utils.all(this.closeWhenHidden, this.header).map((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === 2) {
      return
    }

    document
      .querySelector(':root')
      .style.setProperty(
        `--hh-announcement-height`,
        `${this._announcementHeight}px`
      )

    this._state = 2
  }

  reset() {
    if (this._state === 0) {
      return
    }
    this._state = 0
  }

  render() {
    return html`<div
      class="container"
      style="${styleMap({
        transform:
          this._state === 1
            ? `translateY(-${this._height}px)`
            : 'translateY(0)',
        background: this._scrollTop > 0 ? 'white' : 'transparent',
      })}"
    >
      <slot></slot>
    </div> `
  }

  static styles = css`
    .container {
      position: fixed;
      top: 0;
      transform: translateY(0);
      transition:
        transform var(--hh-duration-long, 500ms),
        background var(--hh-duration-long, 500ms);
      width: 100%;
      z-index: 1;
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)
